<template>
  <div class="off-canvas" :class="{ 'off-canvas--collapsed': collapsed }">
    <div class="off-canvas__overlay z-9998" />

    <div
      ref="offCanvasMenu"
      v-off-canvas="visibility"
      class="off-canvas__menu"
      data-off-canvas=".off-canvas"
      data-overlay=".off-canvas__overlay"
      data-nav-button=".ui-toolbar-nav-icon"
      data-opened-class="off-canvas--opened"
      data-animate-class="off-canvas--animatable"
    >
      <slot name="brand" />
      <slot name="account" />
      <slot name="navigation" />
      <slot name="touchpoint" />
    </div>
  </div>
</template>

<script>
import offCanvas from '@/directives/offcanvas';

export default {
  name: 'OffCanvas',

  directives: {
    offCanvas,
  },

  props: {
    visibility: {
      type: Boolean,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      closingHandler: null,
    };
  },

  created() {
    // to prevent `this` keyword of closeOffCanvas to get bound by addEventListener
    this.closingHandler = e => this.closeOffCanvas(e);
  },

  mounted() {
    // bind a handler to the document to close the offCanvas menu
    document.addEventListener('click', this.closingHandler, false);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closingHandler, false);
  },

  methods: {
    updateVisibility(visibility) {
      this.$emit('changed', visibility);
    },

    closeOffCanvas(e) {
      const { target } = e;
      const menu = this.$refs.offCanvasMenu;
      const menuData = menu.dataset;
      const navBtnSelector = menuData.navButton;
      const offCanvasElm = document.querySelector(menuData.offCanvas);

      // check if the target is not the offCanvas menu nor the nav button
      const isOutside = !(
        target.closest(`.${menu.className}`) || target.closest(navBtnSelector)
      );
      const offCanvasIsOpen = offCanvasElm.classList.contains(
        menuData.openedClass,
      );

      if (offCanvasIsOpen && isOutside) {
        // change the offCanvas visibility to false
        this.updateVisibility(false);
      }
    },
  },
};
</script>

<style lang="scss">
/* stylelint-disable plugin/no-unsupported-browser-features */
.off-canvas {
  $block: '.off-canvas';

  &__menu {
    width: $lb-off-canvas-width;
    height: 100%;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    border-right: $lb-off-canvas-side-border;
    color: $lb-off-canvas-color;
    background-color: $lb-off-canvas-bg-color;
    transition: 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transition-property: transform, visibility, width;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* enables momentum scrolling in iOS overflow elements */
    transform: translate3d(-100%, 0, 0);
    visibility: hidden;
    will-change: transform, width;
    display: flex;
    flex-direction: column;

    > * {
      flex-shrink: 0;
    }

    @include rtl() {
      left: auto;
      right: 0;
      border-right: 0;
      border-left: $lb-off-canvas-side-border;
      transform: translate3d(100%, 0, 0);
    }

    @include breakpoint(large) {
      transform: none;
      visibility: visible;
      z-index: auto;

      @include rtl() {
        transform: none;
      }
    }

    &::-webkit-scrollbar {
      background: #000;
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background: #eee;
    }

    &::-webkit-scrollbar-thumb {
      background: #ddd;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #ccc;
    }
  }

  &__overlay {
    display: none;
  }

  &--animatable {
    #{$block}__menu {
      visibility: visible;

      transition: 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      transition-property: transform, visibility;

      // improves performance issues on mobile
      backface-visibility: hidden;
      perspective: 1000;
    }
  }

  &--opened {
    #{$block}__menu {
      /* Visual */
      visibility: visible;
      box-shadow: $lb-off-canvas-shadow;

      /* Misc */
      transform: translate3d(0, 0, 0);

      @include breakpoint(large) {
        box-shadow: none;
      }
    }

    #{$block}__overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;

      display: block;

      background-color: $lb-off-canvas-overlay-bg-color;
      // workaround for triggering click event on apple devices
      cursor: pointer;

      animation: fade 500ms;
      will-change: opacity;

      @include breakpoint(large) {
        display: none;
      }
    }
  }

  &--collapsed {
    #{$block}__menu {
      width: $lb-off-canvas-collapsed-width;
    }
  }

  @include breakpoint(large) {
    &--stuck {
      #{$block}__menu {
        position: fixed;
        top: 0;
      }
    }
  }
}

@keyframes fade {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
